import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  Chip,
  OutlinedInput,
  Collapse,
  IconButton,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Stack,
  Badge,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Sort,
  FilterAlt,
  TuneRounded,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LEAD_STATUS, LEAD_STATUS_LABELS, LEAD_PRIORITY, LEAD_PRIORITY_LABELS } from '../../services/leads';
import { formatDateForAPI } from '../../utils/helpers';

const LeadsFilter = ({ onFiltersChange, initialFilters = {} }) => {
  const { states, diseases, users } = useSelector((state) => state.users);
  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    state: '',
    disease: '',
    assigned_to: '',
    created_at_start: null,
    created_at_end: null,
    follow_up_date_start: null,
    follow_up_date_end: null,
    name: '',
    phone: '',
    email: '',
    city_name: '',
    disease_name: '',
    ordering: '-created_at',
    ...initialFilters
  });

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    
    // Clean empty values before sending
    const cleanFilters = Object.entries(newFilters).reduce((acc, [key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        // Special handling for arrays - only include if not empty
        if (Array.isArray(val)) {
          if (val.length > 0) {
            acc[key] = val;
          }
        } else if (val instanceof Date) {
          // Format dates as YYYY-MM-DD for API using local timezone
          acc[key] = formatDateForAPI(val);
        } else {
          acc[key] = val;
        }
      }
      return acc;
    }, {});
    
    onFiltersChange(cleanFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      status: '',
      priority: '',
      state: '',
      disease: '',
      assigned_to: '',
      created_at_start: null,
      created_at_end: null,
      follow_up_date_start: null,
      follow_up_date_end: null,
      name: '',
      phone: '',
      email: '',
      city_name: '',
      disease_name: '',
      ordering: '-created_at',
    };
    setFilters(clearedFilters);
    onFiltersChange({ ordering: '-created_at' });
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      // Exclude ordering from active count as it's always set
      if (key === 'ordering') return false;
      
      if (typeof value === 'string') {
        return value !== '';
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined;
    }).length;
  };

  const sortingOptions = [
    { value: '-created_at', label: 'Newest First' },
    { value: 'created_at', label: 'Oldest First' },
    { value: '-updated_at', label: 'Recently Updated' },
    { value: 'name', label: 'Name A-Z' },
    { value: '-name', label: 'Name Z-A' },
    { value: '-priority', label: 'High Priority First' },
    { value: 'priority', label: 'Low Priority First' },
    { value: '-follow_up_date', label: 'Follow-up Date (Latest)' },
    { value: 'follow_up_date', label: 'Follow-up Date (Earliest)' },
  ];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TuneRounded color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="600">Lead Filters</Typography>
              <Typography variant="body2" color="text.secondary">
                Filter and search through your leads database
              </Typography>
            </Box>
            {getActiveFiltersCount() > 0 && (
              <Badge 
                badgeContent={getActiveFiltersCount()} 
                color="primary"
                sx={{ ml: 1 }}
              >
                <FilterAlt color="action" />
              </Badge>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<Clear />}
              onClick={handleClearFilters}
              variant="outlined"
              size="small"
              color="error"
            >
              Clear All
            </Button>
            <Button
              startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setExpanded(!expanded)}
              variant="outlined"
              size="small"
            >
              {expanded ? 'Less Filters' : 'More Filters'}
            </Button>
          </Box>
        </Box>

        {/* Quick Search & Sort */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Global Search"
              placeholder="Search leads by name, phone, or email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.entries(LEAD_STATUS).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    {LEAD_STATUS_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                label="Sort By"
                startAdornment={<Sort sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                {sortingOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Advanced Filters */}
        <Collapse in={expanded}>
          <Stack spacing={3}>
            {/* Individual Search Fields */}
            <Box>
              <Typography variant="h6" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search fontSize="small" />
                Specific Field Search
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Name Contains"
                    placeholder="e.g. raushan"
                    value={filters.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Phone Contains"
                    placeholder="e.g. 7091"
                    value={filters.phone}
                    onChange={(e) => handleFilterChange('phone', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Email Contains"
                    placeholder="e.g. @gmail.com"
                    value={filters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="City Name Contains"
                    placeholder="e.g. mumbai, pat"
                    value={filters.city_name}
                    onChange={(e) => handleFilterChange('city_name', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Disease Name Contains"
                    placeholder="e.g. diabetes, zenova"
                    value={filters.disease_name}
                    onChange={(e) => handleFilterChange('disease_name', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Status and Priority */}
            <Box>
              <Typography variant="h6" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAlt fontSize="small" />
                Priority & Classification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority Level</InputLabel>
                    <Select
                      value={filters.priority}
                      onChange={(e) => handleFilterChange('priority', e.target.value)}
                      label="Priority Level"
                    >
                      <MenuItem value="">All Priorities</MenuItem>
                      {Object.entries(LEAD_PRIORITY).map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {LEAD_PRIORITY_LABELS[value]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Assignment Filters */}
            <Box>
              <Typography variant="h6" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList fontSize="small" />
                Assignment & Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Specific State</InputLabel>
                    <Select
                      value={filters.state}
                      onChange={(e) => handleFilterChange('state', e.target.value)}
                      label="Specific State"
                    >
                      <MenuItem value="">All States</MenuItem>
                      {Array.isArray(states) && states.map((state) => (
                        <MenuItem key={state.id} value={state.id}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Specific Disease</InputLabel>
                    <Select
                      value={filters.disease}
                      onChange={(e) => handleFilterChange('disease', e.target.value)}
                      label="Specific Disease"
                    >
                      <MenuItem value="">All Diseases</MenuItem>
                      {Array.isArray(diseases) && diseases.map((disease) => (
                        <MenuItem key={disease.id} value={disease.id}>
                          {disease.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      value={filters.assigned_to}
                      onChange={(e) => handleFilterChange('assigned_to', e.target.value)}
                      label="Assigned To"
                    >
                      <MenuItem value="">All Users</MenuItem>
                      {Array.isArray(users) && users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.role})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Date Filters */}
            <Box>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                📅 Date Range Filters
              </Typography>
              <Grid container spacing={2}>
          
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Created From"
              value={filters.created_at_start}
              onChange={(date) => handleFilterChange('created_at_start', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Created To"
              value={filters.created_at_end}
              onChange={(date) => handleFilterChange('created_at_end', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Follow-up From"
              value={filters.follow_up_date_start}
              onChange={(date) => handleFilterChange('follow_up_date_start', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="Follow-up To"
              value={filters.follow_up_date_end}
              onChange={(date) => handleFilterChange('follow_up_date_end', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
              </Grid>
            </Box>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default LeadsFilter;